import { useState } from 'react';
import { Chevron, COLLAPSE_APPS_ABOVE } from './AppsDisclosure';

interface SummaryOutcome {
  code: string;
  displayName: string;
}

interface OutcomesSummaryProps {
  outcomes: SummaryOutcome[];
  /** id of the chip list the bar shows and hides. */
  listId: string;
}

function CheckDot({ className }: { className: string }) {
  return (
    <span className={`flex flex-none items-center justify-center rounded-full bg-emerald-500 text-white ${className}`}>
      <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3.5} d="M5 13l4 4L19 7" />
      </svg>
    </span>
  );
}

/**
 * The picked outcomes as chips. A long list starts folded behind a one-line
 * bar — the same pattern as the app list — so it never pushes the plan and
 * the apps off a phone screen.
 */
export function OutcomesSummary({ outcomes, listId }: OutcomesSummaryProps) {
  const [expanded, setExpanded] = useState(false);
  const collapsible = outcomes.length > COLLAPSE_APPS_ABOVE;
  const named = outcomes.slice(0, 2).map((o) => o.displayName);
  const more = outcomes.length - named.length;

  return (
    <>
      {collapsible && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls={listId}
          data-testid="outcomes-disclosure"
          className="flex w-full items-center gap-3 rounded-2xl border border-gray-100 bg-gray-50/70 p-3 text-left transition-colors hover:border-gray-200 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-emerald-400 sm:p-4"
        >
          <span className="relative flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-emerald-50 ring-1 ring-emerald-100 sm:h-10 sm:w-10">
            <CheckDot className="h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold text-gray-900">{outcomes.length} outcomes selected</span>
            <span className="mt-0.5 block text-xs text-gray-500 line-clamp-2 sm:truncate">
              {named.join(', ')}
              {more > 0 ? ` and ${more} more` : ''}
            </span>
          </span>
          <span className="flex flex-none items-center gap-1 text-xs font-semibold text-emerald-700 sm:text-sm">
            {expanded ? 'Hide' : 'Show all'}
            <Chevron expanded={expanded} className="" />
          </span>
        </button>
      )}
      <ul
        id={listId}
        hidden={collapsible && !expanded}
        className={`${collapsible && !expanded ? 'hidden' : 'flex'} flex-wrap gap-1.5 sm:gap-2 ${collapsible ? 'mt-3' : ''}`}
      >
        {outcomes.map((outcome) => (
          <li
            key={outcome.code}
            className="inline-flex items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50/70 py-1 pl-1 pr-2.5 text-xs font-medium text-gray-800 sm:gap-2 sm:py-1.5 sm:pl-1.5 sm:pr-3 sm:text-sm"
          >
            <CheckDot className="h-4 w-4 sm:h-5 sm:w-5" />
            {outcome.displayName}
          </li>
        ))}
      </ul>
    </>
  );
}
